import { Grid, VStack } from "@chakra-ui/react";
import PatientProfileEditModeButton from "../../../../entityProfileModal/components/EntityProfileEditModeButton";
import {
  EntityProfileErrorState,
  EntityProfileLoadingState,
} from "../../../../entityProfileModal/components/EntityProfileFeedback";
import type { PatientId } from "../../../../../shared/schema/schema";
import type { PatientProfilePatient } from "../../../patientProfileModal.types";
import { PatientClinicalFields } from "./components/PatientClinicalFields";
import { StaffingPreferenceColumn } from "./components/StaffingPreferenceColumn";
import { StaffingTextListFields } from "./components/StaffingTextListFields";
import { usePatientProfileStaffingPreferencesController } from "./PatientProfileStaffingPreferencesSection.hooks";
import { staffingPreferenceColumns } from "./staffingPreferences.utils";

type Props = {
  patient?: PatientProfilePatient;
  patientId: PatientId;
};

function PatientProfileStaffingPreferencesContent(props: { patient: PatientProfilePatient }) {
  const controller = usePatientProfileStaffingPreferencesController(props.patient);
  const { staffingPreferencesQuery } = controller;

  if (staffingPreferencesQuery.isLoading) {
    return <EntityProfileLoadingState label="Loading staffing preferences" />;
  }

  if (staffingPreferencesQuery.isError) {
    return (
      <EntityProfileErrorState
        label="Could not load staffing preferences."
        onRetry={() => controller.refetchPreferences()}
      />
    );
  }

  if (controller.preferences === undefined) {
    return <EntityProfileLoadingState label="Loading staffing preferences" />;
  }

  const preferences = controller.preferences;

  return (
    <VStack alignItems="stretch" spacing={7}>
      {controller.canEdit ? (
        <PatientProfileEditModeButton
          isEditing={controller.isEditing}
          label="Edit"
          onClick={controller.toggleEditMode}
        />
      ) : null}
      <Grid gap={8} templateColumns={{ base: "1fr", lg: "repeat(3, minmax(0, 1fr))" }}>
        {staffingPreferenceColumns.map((fields, index) => (
          <StaffingPreferenceColumn
            key={index}
            draft={controller.draft}
            fields={fields}
            preferences={preferences}
            onChangeDraft={controller.setDraft}
            onCommitDraft={controller.commitDraft}
          />
        ))}
      </Grid>

      <PatientClinicalFields
        allergyOptions={controller.allergyOptions}
        isEditing={controller.isEditing}
        isLoadingAllergies={controller.isAllergyOptionsLoading}
        patient={props.patient}
        patientFieldsDraft={controller.patientFieldsDraft}
        onChangePatientFieldsDraft={controller.setPatientFieldsDraft}
        onCommitAllergies={controller.commitAllergies}
        onCommitMedicalNotes={controller.commitMedicalNotes}
      />

      <StaffingTextListFields
        draft={controller.draft}
        preferences={preferences}
        onChangeDraft={controller.setDraft}
        onCommitDraft={controller.commitDraft}
      />
    </VStack>
  );
}

export default function PatientProfileStaffingPreferencesSection(props: Props) {
  if (props.patient === undefined) {
    return <EntityProfileLoadingState label="Loading staffing preferences" />;
  }

  return <PatientProfileStaffingPreferencesContent patient={props.patient} />;
}
